import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useAppStore = create(
    persist(
        (set, get) => ({
            // Current project
            currentProjectId: null,
            setCurrentProjectId: (id) => set({ currentProjectId: id }),

            // Onboarding state
            hasCompletedOnboarding: false,
            setHasCompletedOnboarding: (value) => set({ hasCompletedOnboarding: value }),

            // UI state
            sidebarCollapsed: false,
            toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

            aiPanelCollapsed: false,
            toggleAiPanel: () => set((state) => ({ aiPanelCollapsed: !state.aiPanelCollapsed })),

            aiPanelWidth: 340,
            setAiPanelWidth: (width) => set({ aiPanelWidth: width }),

            // Focus mode (hides both panels)
            focusMode: false,
            toggleFocusMode: () => set((state) => ({
                focusMode: !state.focusMode,
                sidebarCollapsed: !state.focusMode ? true : state.sidebarCollapsed,
                aiPanelCollapsed: !state.focusMode ? true : state.aiPanelCollapsed
            })),

            // Theme (for future light mode support)
            theme: 'dark',
            setTheme: (theme) => set({ theme }),

            // Language preference
            language: 'en',
            setLanguage: (lang) => set({ language: lang }),

            // RTL preference per project (persisted)
            projectRtl: {}, // { [projectId]: boolean }
            getProjectRtl: (projectId) => get().projectRtl[projectId] || false,
            setProjectRtl: (projectId, isRtl) => set((state) => ({
                projectRtl: { ...state.projectRtl, [projectId]: isRtl }
            })),

            // Daily writing goal
            dailyGoal: 500,
            setDailyGoal: (goal) => set({ dailyGoal: goal }),

            wordsWrittenToday: 0,
            addWordsWritten: (count) => set((state) => ({
                wordsWrittenToday: state.wordsWrittenToday + count
            })),
            resetDailyWords: () => set({ wordsWrittenToday: 0 }),

            // Streak tracking
            currentStreak: 0,
            longestStreak: 0,
            // Array of 7 booleans — index 0 = 6 days ago, index 6 = today
            weeklyLog: [false, false, false, false, false, false, false],

            // Last reset date for daily goal
            lastResetDate: null,
            setLastResetDate: (date) => set({ lastResetDate: date }),

            // Check and reset daily words if new day + update streaks
            checkDailyReset: () => {
                const today = new Date().toDateString();
                const lastReset = get().lastResetDate;
                if (lastReset !== today) {
                    const wroteYesterday = get().wordsWrittenToday > 0;
                    const prevStreak = get().currentStreak;
                    const prevLongest = get().longestStreak;
                    const prevWeekly = [...get().weeklyLog];

                    // Shift weekly log left — drop oldest, push yesterday's result
                    const newWeekly = [...prevWeekly.slice(1), wroteYesterday];

                    let newStreak = prevStreak;
                    if (wroteYesterday) {
                        newStreak = prevStreak + 1;
                    } else {
                        newStreak = 0;
                    }

                    set({
                        wordsWrittenToday: 0,
                        lastResetDate: today,
                        currentStreak: newStreak,
                        longestStreak: Math.max(prevLongest, newStreak),
                        weeklyLog: newWeekly,
                    });
                }
            },

            // Reset state on sign out
            reset: () =>
                set({
                    currentProjectId: null,
                    hasCompletedOnboarding: false,
                    sidebarCollapsed: false,
                    aiPanelCollapsed: false,
                    aiPanelWidth: 340,
                    focusMode: false,
                    theme: 'dark',
                    language: 'en',
                    dailyGoal: 500,
                    wordsWrittenToday: 0,
                    lastResetDate: null,
                    currentStreak: 0,
                    longestStreak: 0,
                    weeklyLog: [false, false, false, false, false, false, false],
                })
        }),
        {
            name: 'share-pen-app-storage',
            partialize: (state) => ({
                currentProjectId: state.currentProjectId,
                hasCompletedOnboarding: state.hasCompletedOnboarding,
                sidebarCollapsed: state.sidebarCollapsed,
                aiPanelCollapsed: state.aiPanelCollapsed,
                aiPanelWidth: state.aiPanelWidth,
                focusMode: state.focusMode,
                theme: state.theme,
                language: state.language,
                projectRtl: state.projectRtl,
                dailyGoal: state.dailyGoal,
                wordsWrittenToday: state.wordsWrittenToday,
                lastResetDate: state.lastResetDate,
                currentStreak: state.currentStreak,
                longestStreak: state.longestStreak,
                weeklyLog: state.weeklyLog,
            })
        }
    )
);

export default useAppStore;
